import { FEATURE_LINE_PICK_RADIUS_PX } from '../../constants/solidworks-picking';

/**
 * SolidWorks-like 默认视觉预设（基于产品截图冻结的 baseline）。
 * 该文件是渲染与交互颜色 token 的唯一真源。
 */

export type Vector3Tuple = [number, number, number];

export interface SolidworksDefaultPreset {
    viewport: {
        backgroundTop: number;
        backgroundBottom: number;
    };
    model3D: {
        baseFaceColor: number;
        visibleEdgeColor: number;
        visibleEdgeOpacity: number;
        silhouetteEdgeColor: number;
    };
    selection3D: {
        hoverFaceFill: number;
        selectedFaceFill: number;
        hoverEdgeColor: number;
        selectedEdgeColor: number;
        vertexCueColor: number;
        vertexCueRadius: number;
    };
    sketchHost: {
        hostFaceBoundaryColor: number;
        hostFaceBoundaryWidth: number;
        hostFaceFillMode: 'preserve-base-face';
    };
    sketchEntity: {
        underDefinedColor: number;
        selectedColor: number;
        fullyDefinedColor: number;
        overDefinedColor: number;
        constructionColor: number;
        originColor: number;
        previewColor: number;
    };
    featurePreview: {
        bossPreviewColor: number;
        cutPreviewColor: number;
    };
    picking: {
        edgePickThresholdPx: number;
        vertexPickThresholdPx: number;
        occlusionEpsilon: number;
    };
    selectionBox: {
        dashSize: number;
        gapSize: number;
        /** 窗口选（从左到右）边框颜色 */
        windowBorderColor: number;
        /** 交叉选（从右到左）边框颜色 */
        crossingBorderColor: number;
    };
    auxiliaryLine: {
        /** 辅助线（Ctrl+点击多选等）高亮颜色 */
        highlightColor: number;
    };
    selectionHandle: {
        borderColor: number;
        fillColor: number;
        size: number;
        hitSize: number;
    };
    renderer: {
        toneMapping: 'none';
        exposure: number;
    };
    lighting: {
        ambientColor: number;
        ambientIntensity: number;
        /** 环境光暗色（用于无自定义海拘时的默认加载） */
        ambientDarkColor: number;
        directionalColor: number;
        directionalIntensity: number;
        directionalPosition: Vector3Tuple;
        /** 补光颜色 */
        fillLightColor: number;
        fillLightIntensity: number;
        fillLightPosition: Vector3Tuple;
        /** 反向补光颜色 */
        backLightColor: number;
        backLightIntensity: number;
        backLightPosition: Vector3Tuple;
        /** 相机同步光组距离 */
        cameraSynchronizedLightDistance: number;
    };
    material: {
        baseMetalness: number;
        baseRoughness: number;
        hoverEmissiveIntensity: number;
        selectedEmissiveIntensity: number;
        defaultOpacity: number;
        /** 默认零件颜色（白色） */
        defaultColor: number;
        /** 选中状态颜色 */
        selectedColor: number;
        /** 悬停状态颜色 */
        hoveredColor: number;
        /** 金属材质颜色 */
        metalColor: number;
        /** 塑料材质颜色 */
        plasticColor: number;
        /** 玻璃材质颜色 */
        glassColor: number;
        /** 木质材质颜色 */
        woodColor: number;
    };
}

export const solidworksDefaultPreset: SolidworksDefaultPreset = {
    viewport: {
        backgroundTop: 0xe0e3ed,
        backgroundBottom: 0xfdfdfe,
    },
    model3D: {
        baseFaceColor: 0xffffff,
        visibleEdgeColor: 0x111318,
        visibleEdgeOpacity: 1,
        silhouetteEdgeColor: 0x111318,
    },
    selection3D: {
        hoverFaceFill: 0x2ea8ff,
        selectedFaceFill: 0x0093ff,
        hoverEdgeColor: 0xf48611,
        selectedEdgeColor: 0x0043ff,
        vertexCueColor: 0xf4c542,
        vertexCueRadius: 0.14,
    },
    sketchHost: {
        hostFaceBoundaryColor: 0xf48611,
        hostFaceBoundaryWidth: 2,
        hostFaceFillMode: 'preserve-base-face',
    },
    sketchEntity: {
        underDefinedColor: 0x53a9ff,
        selectedColor: 0x8fd3ff,
        fullyDefinedColor: 0x000000,
        overDefinedColor: 0xff0000,
        constructionColor: 0x7a7a7a,
        originColor: 0xff3b30,
        previewColor: 0x53a9ff,
    },
    featurePreview: {
        bossPreviewColor: 0xf3f4f6,
        cutPreviewColor: 0xd9534f,
    },
    picking: {
        edgePickThresholdPx: FEATURE_LINE_PICK_RADIUS_PX,
        vertexPickThresholdPx: 10,
        occlusionEpsilon: 0.002,
    },
    selectionBox: {
        dashSize: 2.5,
        gapSize: 1.5,
        windowBorderColor: 0x4a9eff,
        crossingBorderColor: 0xf59e0b,
    },
    auxiliaryLine: {
        highlightColor: 0x3b82f6,
    },
    selectionHandle: {
        borderColor: 0x0093ff,
        fillColor: 0xffffff,
        size: 11,
        hitSize: 18,
    },
    renderer: {
        toneMapping: 'none',
        exposure: 1,
    },
    lighting: {
        ambientColor: 0xd8d8d8,
        ambientIntensity: 1.6,
        ambientDarkColor: 0x404040,
        directionalColor: 0xd8d8d8,
        directionalIntensity: 1.8,
        directionalPosition: [100, 100, 100],
        fillLightColor: 0xd8d8d8,
        fillLightIntensity: 1.1,
        fillLightPosition: [-100, 100, -100],
        backLightColor: 0xd8d8d8,
        backLightIntensity: 0.8,
        backLightPosition: [100, -60, -100],
        cameraSynchronizedLightDistance: 100,
    },
    material: {
        baseMetalness: 0.5,
        baseRoughness: 0.5,
        hoverEmissiveIntensity: 0.12,
        selectedEmissiveIntensity: 0.18,
        defaultOpacity: 1,
        defaultColor: 0xffffff,
        selectedColor: 0xffaa00,
        hoveredColor: 0x88ccff,
        metalColor: 0xe2e4ea,
        plasticColor: 0xf3f4f6,
        glassColor: 0xd8ebff,
        woodColor: 0x8b5a3c,
    },
};

export function colorHexToCss(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`;
}

export const solidworksDefaultPresetCss = {
    viewportBackgroundTop: colorHexToCss(solidworksDefaultPreset.viewport.backgroundTop),
    viewportBackgroundBottom: colorHexToCss(solidworksDefaultPreset.viewport.backgroundBottom),
    modelBaseFaceColor: colorHexToCss(solidworksDefaultPreset.model3D.baseFaceColor),
    visibleEdgeColor: colorHexToCss(solidworksDefaultPreset.model3D.visibleEdgeColor),
    selectedFaceFill: colorHexToCss(solidworksDefaultPreset.selection3D.selectedFaceFill),
    hoverFaceFill: colorHexToCss(solidworksDefaultPreset.selection3D.hoverFaceFill),
    hoverEdgeColor: colorHexToCss(solidworksDefaultPreset.selection3D.hoverEdgeColor),
    selectedEdgeColor: colorHexToCss(solidworksDefaultPreset.selection3D.selectedEdgeColor),
    vertexCueColor: colorHexToCss(solidworksDefaultPreset.selection3D.vertexCueColor),
    hostFaceBoundaryColor: colorHexToCss(solidworksDefaultPreset.sketchHost.hostFaceBoundaryColor),
    sketchUnderDefinedColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.underDefinedColor),
    sketchSelectedColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.selectedColor),
    sketchFullyDefinedColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.fullyDefinedColor),
    sketchOverDefinedColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.overDefinedColor),
    sketchConstructionColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.constructionColor),
    sketchOriginColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.originColor),
    sketchPreviewColor: colorHexToCss(solidworksDefaultPreset.sketchEntity.previewColor),
    bossPreviewColor: colorHexToCss(solidworksDefaultPreset.featurePreview.bossPreviewColor),
    cutPreviewColor: colorHexToCss(solidworksDefaultPreset.featurePreview.cutPreviewColor),
    selectionBoxWindowColor: colorHexToCss(solidworksDefaultPreset.selectionBox.windowBorderColor),
    selectionBoxCrossingColor: colorHexToCss(solidworksDefaultPreset.selectionBox.crossingBorderColor),
    selectionBoxWindowBg: `rgba(${(solidworksDefaultPreset.selectionBox.windowBorderColor >> 16) & 0xff}, ${(solidworksDefaultPreset.selectionBox.windowBorderColor >> 8) & 0xff}, ${solidworksDefaultPreset.selectionBox.windowBorderColor & 0xff}, 0.15)`,
    selectionBoxCrossingBg: `rgba(${(solidworksDefaultPreset.selectionBox.crossingBorderColor >> 16) & 0xff}, ${(solidworksDefaultPreset.selectionBox.crossingBorderColor >> 8) & 0xff}, ${solidworksDefaultPreset.selectionBox.crossingBorderColor & 0xff}, 0.12)`,
};
